/**
 * Shared upload fields for the tool-based board upload — a tool selector
 * (KiCad / EasyEDA / Altium-disabled) plus one DropZone per file slot the
 * chosen tool declares. Fully controlled: the parent owns the tool +
 * per-slot file state and renders the surrounding dialog chrome.
 *
 * Used by AddBoardDialog and both UploadVersionDialogs so the slot model
 * stays defined in exactly one place.
 */
import { DropZone } from "@features/projects/components/list/DropZone";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  SOURCE_TOOLS,
  type SlotFiles,
  type SlotKey,
  type SourceTool,
  getTool,
} from "./tools";

interface ToolUploadFieldsProps {
  tool: SourceTool;
  onToolChange: (tool: SourceTool) => void;
  files: SlotFiles;
  onFileChange: (key: SlotKey, file: File | null) => void;
  /** Per-slot validation errors (e.g. wrong extension / too large). */
  errors?: Partial<Record<SlotKey, string | null>>;
  disabled?: boolean;
}

export function ToolUploadFields({
  tool,
  onToolChange,
  files,
  onFileChange,
  errors,
  disabled,
}: ToolUploadFieldsProps) {
  const def = getTool(tool);

  return (
    <>
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Source tool</InputLabel>
        <Select
          value={tool}
          label="Source tool"
          onChange={(e) => onToolChange(e.target.value as SourceTool)}
        >
          {SOURCE_TOOLS.map((t) => (
            <MenuItem key={t.id} value={t.id} disabled={t.disabled}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{def.hint}</FormHelperText>
      </FormControl>

      {def.slots.map((slot) => {
        const err = errors?.[slot.key];
        return (
          <div key={slot.key}>
            <DropZone
              accept={slot.accept}
              label={`Drop ${slot.label}${slot.required ? "" : ""}`}
              file={files[slot.key] ?? null}
              onFile={(f) => onFileChange(slot.key, f)}
            />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                color: err ? "error.main" : "text.disabled",
              }}
            >
              {err ?? slot.hint}
            </Typography>
          </div>
        );
      })}
    </>
  );
}
